import { Button, CircularProgress, Typography } from '@mui/material';
import { useToast } from '../../../Hooks/useToast';
import { useUser } from '../../../Hooks/useUser';
import {
  stripeApi,
  useDeleteDefaultPaymentMethodMutation,
} from '../../../Redux/api/stripeApi';
import { useAppDispatch } from '../../../Redux/store';
import { KaziModal } from '../../../Shared Components/Kazi/KaziModal';

interface RemoveCardConfirmModalProps {
  open: boolean;
  handleClose: () => void;
}
export const RemoveCardConfirmModal = ({
  open,
  handleClose,
}: RemoveCardConfirmModalProps) => {
  /* ******************** Hooks ******************** */
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const { invalidateUser, user } = useUser();

  const [deleteDefaultPaymentMethod, { isLoading: isRemoving }] =
    useDeleteDefaultPaymentMethodMutation();

  /* ******************** Variables ******************** */
  const hasActiveSubscription = user?.subscription?.renew === true;

  /* ******************** Functions ******************** */
  const handleDeleteDefaultPaymentMethod = () => {
    deleteDefaultPaymentMethod()
      .unwrap()
      .then(() => {
        invalidateUser();

        handleClose();

        showToast({
          text: 'Your card has been removed.',
        });

        dispatch(stripeApi.util.invalidateTags(['DefaultPaymentMethod']));
      })
      .catch(err => {
        console.error('Error occured while cancelling subscription: ', err);
        let message = 'An error occured while removing the card';

        if (err?.status === 400) {
          message = err.data.message;
        }

        showToast({
          text: message,
          variant: 'error',
        });
      });
  };
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  return (
    <KaziModal
      open={open}
      handleClose={handleClose}
      title={
        hasActiveSubscription
          ? 'You have an active subscription'
          : 'Are you sure you want to remove this card?'
      }
      content={
        <>
          <Typography variant="body-medium">
            {hasActiveSubscription
              ? "You can't remove this card while an active subscription is in place. You'll need to cancel your subscription first and that may interrupt your access to platform benefits"
              : 'Removing this card will prevent you from receiving new subscriptions until you add another payment method.'}
          </Typography>
        </>
      }
      actions={[
        <Button variant="outlined" onClick={handleClose}>
          {hasActiveSubscription ? 'Ok' : 'No, I want to keep it'}
        </Button>,

        !hasActiveSubscription && (
          <Button
            variant="contained"
            disabled={isRemoving}
            onClick={handleDeleteDefaultPaymentMethod}
            sx={{
              '&.Mui-disabled': {
                backgroundColor: 'black.50',
                border: 'none',
                color: 'primary.main',
              },
            }}
            endIcon={
              isRemoving && <CircularProgress color="secondary" size={'18px'} />
            }
          >
            Yes, remove the card
          </Button>
        ),
      ]}
    />
  );
};
